<template>
  <div class="recharge-page">

    <!-- 移动端顶部导航栏 -->
    <div v-if="isMobile" class="mobile-top-header">
      <div class="mobile-header-left">
        <Icon type="ios-menu" class="menu-icon" @click="goBack" />
        <div class="logo-text">抖数君</div>
      </div>
      <div class="mobile-header-right">
        <Select v-model="currentTheme" @on-change="handleThemeChange" size="small" style="width: 100px;">
          <Option v-for="theme in themeOptions" :key="theme.name" :value="theme.name">
            {{ theme.label }}
          </Option>
        </Select>
        <div class="mobile-user-info" @click="toggleUserDropdown">
          <Avatar :src="require('@/assets/images/default-avatar.png')" size="small" />
          <span class="username">{{ username }}</span>
        </div>
      </div>
    </div>

    <!-- 主卡片 -->
    <Card :class="{ disabled: isCardDisabled }"
          class="main-card"
          shadow
    >
      <!-- 当前VIP状态 -->
      <div class="balance-info">
        <h2 class="page-title">充值中心</h2>
        <p v-if="currentVipStatus.is_vip">
          当前状态: <strong style="color: #19be6b;">VIP会员</strong>
          <span v-if="currentVipStatus.expire_time">，到期时间: {{ currentVipStatus.expire_time }}</span>
        </p>
        <p v-else>当前状态: <strong style="color: #ed4014;">普通用户</strong></p>
        <p>当前余额: <strong>{{ userBalance }}</strong> 点</p>
      </div>

      <!-- 充值类型切换 -->
      <div class="recharge-type-switch">
        <Button 
          :type="rechargeType === 'vip' ? 'primary' : 'default'"
          @click="switchRechargeType('vip')"
          size="large"
          :style="allowPointRecharge ? 'margin-right: 10px;' : ''"
        >
          VIP套餐充值
        </Button>
        <Button 
          v-if="allowPointRecharge"
          :type="rechargeType === 'points' ? 'primary' : 'default'"
          @click="switchRechargeType('points')"
          size="large"
        >
          点数充值
        </Button>
      </div>

      <Divider>{{ rechargeType === 'vip' ? '选择VIP套餐' : '选择充值套餐' }}</Divider>

      <!-- VIP套餐充值 -->
      <div v-if="rechargeType === 'vip'">
        <!-- 低请求版套餐列表（300次/天） -->
        <div class="package-category">
          <h3 class="category-title">低请求版 (300次/天)</h3>
          <div class="package-list">
            <div
                v-for="pkg in lowRequestPackages"
                :key="pkg.id"
                :class="['package-item', { active: selectedPackage && selectedPackage.id === pkg.id }]"
                @click="selectPackage(pkg)"
            >
              <div class="package-name">{{ pkg.name }}</div>
              <div class="package-price">￥{{ pkg.price }}</div>
              <div class="package-desc">{{ getPackageDuration(pkg) }}</div>
              <div class="package-limit">每天{{ pkg.daily_limit }}次查询</div>
            </div>
          </div>
        </div>

        <!-- 高请求版套餐列表（450次/天） -->
        <div class="package-category">
          <h3 class="category-title">高请求版 (450次/天)</h3>
          <div class="package-list">
            <div
                v-for="pkg in highRequestPackages"
                :key="pkg.id"
                :class="['package-item', { active: selectedPackage && selectedPackage.id === pkg.id }]"
                @click="selectPackage(pkg)"
            >
              <div class="package-name">{{ pkg.name }}</div>
              <div class="package-price">￥{{ pkg.price }}</div>
              <div class="package-desc">{{ getPackageDuration(pkg) }}</div>
              <div class="package-limit">每天{{ pkg.daily_limit }}次查询</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 点数充值 -->
      <div v-if="rechargeType === 'points'">
        <!-- 充值套餐列表 -->
        <div class="package-list">
          <div
              v-for="(points, amount) in rechargePackages"
              :key="amount"
              :class="['package-item', { active: selectedAmount == amount }]"
              @click="selectPackageByAmount(amount)"
          >
            <div class="package-price">￥{{ amount }}</div>
            <div class="package-points">{{ points }} 点</div>
            <div class="package-unit-price">({{ (amount / points).toFixed(2) }} 元/点)</div>
          </div>
        </div>

        <!-- 自定义金额充值 -->
        <div class="custom-amount-container">
          <span class="custom-amount-label-inline">自定义金额充值:</span>
          <Input
              v-model="customAmount"
              placeholder="输入充值金额(10-1000元)"
              style="width: 200px; white-space: nowrap;"
              type="number"
              @input="showCustomAmountAlert"
          >
            <template #append>元</template>
          </Input>
          <Button
              :disabled="!customAmount || customAmount < 5 || customAmount > 1000"
              type="primary"
              @click="selectCustomAmount"
              style="white-space: nowrap;"
          >
            确认
          </Button>
        </div>
      </div>

      <!-- 已选套餐信息 -->
      <div v-if="rechargeType === 'vip' && selectedPackage" class="selected-package">
        <Alert show-icon type="info">
          您选择 <strong>{{ selectedPackage.name }}</strong> 套餐，价格 <strong>￥{{ selectedPackage.price }}</strong>
        </Alert>
      </div>
      <div v-if="rechargeType === 'points' && selectedAmount" class="selected-package">
        <Alert show-icon type="info">
          您选择充值 <strong>{{ selectedAmount }}</strong> 元，可获得 <strong>{{ getPointsForAmount(selectedAmount) }}</strong> 点
        </Alert>
      </div>

      <!-- 支付方式 -->
      <div v-if="(rechargeType === 'vip' && selectedPackage) || (rechargeType === 'points' && selectedAmount)" class="payment-method">
        <p>选择支付方式:</p>
        <RadioGroup v-model="paymentMethod">
          <Radio class="payment-option" label="alipay">
            <Icon color="#1677ff" size="24" type="logo-alipay"/>
            支付宝
            <img
                alt="支付宝Logo"
                class="payment-logo"
                src="@/assets/images/alipay_logo.png"
            />
          </Radio>
          <Radio class="payment-option" label="wxpay">
            <Icon color="#07c160" size="24" type="logo-wechat"/>
            微信支付
            <img
                alt="微信Logo"
                class="payment-logo"
                src="@/assets/images/wechat_logo.png"
            />
          </Radio>
        </RadioGroup>
      </div>

      <!-- 操作按钮 -->
      <div class="actions">
        <Button
            :disabled="!((rechargeType === 'vip' && selectedPackage) || (rechargeType === 'points' && selectedAmount)) || isCardDisabled"
            :loading="payLoading"
            size="large"
            type="primary"
            @click="openPaymentConfirm"
            class="btn-pay"
        >
          <Icon type="ios-card" class="btn-icon"/>
          {{ rechargeType === 'vip' ? '确认购买' : '立即充值' }}
        </Button>

        <Button
            :disabled="isCardDisabled"
            @click="resetSelection"
            class="btn-reset"
        >
          <Icon type="ios-refresh" class="btn-icon"/>
          重新选择
        </Button>

        <Button
            :disabled="isCardDisabled"
            @click="openRechargeList"
            class="btn-records"
        >
          <Icon type="ios-list-box" class="btn-icon"/>
          充值记录
        </Button>
      </div>
      <!-- 添加倒计时显示 -->
      <div v-if="countdownTime > 0 && paymentStatus === 'pending'" class="countdown-timer">
        <p>请不要关闭页面 支付倒计时: <strong>{{ formatCountdownTime }}</strong></p>
      </div>
    </Card>

    <!-- 支付确认Modal -->
    <Modal
        v-model="showPayModal"
        :loading="payModalLoading"
        title="支付确认"
        @on-ok="confirmPay"
        @on-cancel="cancelPay"
    >
      <div class="pay-modal-content">
        <template v-if="rechargeType === 'vip'">
          <p v-if="selectedPackage">您即将购买 <strong>{{ selectedPackage.name }}</strong> 套餐</p>
          <p v-if="selectedPackage">价格: <strong>￥{{ selectedPackage.price }}</strong></p>
          <p v-else>请选择套餐</p>
        </template>
        <template v-else>
          <p>您即将充值 <strong>{{ selectedAmount }}</strong> 元</p>
          <p>可获得 <strong>{{ getPointsForAmount(selectedAmount) }}</strong> 点</p>
        </template>
        <p>支付方式: <strong>{{ paymentMethod === 'alipay' ? '支付宝' : '微信支付' }}</strong></p>
        <Alert show-icon style="margin-top: 15px;" type="warning">
          确认支付后将跳转到支付页面，请在新窗口中完成支付
        </Alert>
      </div>

    </Modal>

    <!-- 支付结果Modal -->
    <!--    作用：Modal 是一个弹出框组件，用于显示支付结果。-->
    <!--    属性说明：-->
    <!--    v-model="showResultModal"：控制模态框的显示与隐藏。showResultModal 是一个布尔值，当为 true 时显示模态框，为 false 时隐藏。-->
    <!--    :footer-hide="true"：隐藏默认的底部操作栏。-->
    <!--    :mask-closable="true"：允许点击遮罩层关闭模态框。-->
    <!--    title="支付结果"：设置模态框的标题为“支付结果”。-->
    <!--    width="360"：设置模态框的宽度为 360 像素。-->
    <Modal
        v-model="showResultModal"
        :footer-hide="true"
        :mask-closable="true"
        title="支付结果"
        width="360"
    >
      <div class="result-modal-content" style="text-align: center;">
        <!-- 成功图标 -->
        <Icon
            v-if="payResult === 'success'"
            size="50" style="color: #19be6b;"
            type="ios-checkmark-circle"
        />
        <!-- 失败图标 -->
        <Icon
            v-else-if="payResult === 'fail'"
            size="50" style="color: #ed4014;"
            type="ios-close-circle"
        />
        <!-- 提示信息 -->
        <p style="font-size: 16px; margin: 15px 0;">{{ resultMessage }}</p>


        <!-- 成功时的操作按钮 -->
        <Button
            v-if="payResult === 'success'"
            type="primary"
            @click="closeResultModal"
        >
          确定
        </Button>
        <!-- 失败时的操作按钮 -->
        <div v-else-if="payResult === 'fail'">
          <Button @click="retryPay">重新支付</Button>
          <Button style="margin-left: 10px;" type="primary" @click="closeResultModal">
            关闭
          </Button>
        </div>
      </div>


    </Modal>
    <!-- 支付结果Modal -->

  </div>
</template>

<script>
import {createRechargeOrder, getRechargePackages, getUserBalance, queryRechargeOrder} from '@/api/recharge'; // 引入API方法
import apiClient from '@/api/index'; // 引入API方法
import QRCode from 'qrcode';
import {Card, Divider, Icon, Modal, Radio} from "view-ui-plus";

export default {
  name: 'Recharge',
  components: {Modal, Icon, Radio, Card, Divider},
  // 组件名称
  data() { // 定义组件数据
    return {
      // 支付相关状态
      qrcodeUrl: '', // 存储后端返回的 qrcode_url
      payUrl: '', // 支付链接
      orderId: '', // 订单ID
      paymentStatus: 'pending', // 支付状态：pending / success / fail
      isPaying: false, // 是否正在支付
      showPayModal: false, // 是否显示支付确认模态框
      showResultModal: false, // 是否显示支付结果模态框
      
      // VIP套餐相关
      vipPackages: [], // VIP套餐数据
      lowRequestPackages: [], // 低请求版套餐
      highRequestPackages: [], // 高请求版套餐
      currentVipStatus: {
        is_vip: false,
        expire_time: ''
      }, // 当前VIP状态
      selectedPackage: null, // 已选VIP套餐
      
      // 点数充值相关
      rechargePackages: {}, // 点数充值套餐数据
      userBalance: 0, // 用户余额
      selectedAmount: 0, // 已选金额
      customAmount: '', // 自定义金额
      isFirstInput: true, // 是否是首次输入标志
      
      paymentMethod: 'alipay', // 支付方式，默认支付宝
      payLoading: false, // 支付加载状态
      payModalLoading: false, // 支付确认模态框加载状态
      payResult: '', // 支付结果（'success' 或 'fail'）

      // 支付倒计时
      countdownTime: 0, // 倒计时时间（秒，10分钟）
      countdownInterval: null, // 倒计时定时器

      // 轮询相关
      pollingInterval: null, // 轮询定时器
      pollCount: 0, // 轮询次数
      maxPollAttempts: 120, // 最大轮询次数（每5秒一次，共10分钟）

      // 提示信息
      resultMessage: '', // 支付结果消息

      // 其他数据...
      isUserLoggedIn: false,
      deviceType: 'mobile', // 默认自动判断  移动端还是pc端
      isCardDisabled: false, // 主卡片是否禁用
      rechargeType: 'vip', // 充值类型：vip-套餐充值, points-点数充值
      siteInfo: { site_name: '' }, // 站点信息（后台 API 返回，用于动态标题）
      allowPointRecharge: false, // 是否允许点数充值（后台控制）
      
      // 移动端导航栏相关
      username: localStorage.getItem('username') || '用户', // 用户名
      currentTheme: localStorage.getItem('currentTheme') || 'default', // 当前主题
      themeOptions: [
        { name: 'default', label: '经典' },
        { name: 'starry', label: '星空' },
        { name: 'blue', label: '蓝色' },
        { name: 'purple', label: '紫色' },
        { name: 'green', label: '绿色' },
        { name: 'red', label: '红色' },
        { name: 'orange', label: '橙色' }
      ], // 主题选项
    };
  },
  // ：created 是 Vue 实例生命周期中的一个钩子函数，在实例创建完成后立即调用
  created() {
    this.detectDeviceType();
  },
  computed: {
    formatCountdownTime() {
      const minutes = Math.floor(this.countdownTime / 60);
      const seconds = this.countdownTime % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    },
  },
  mounted() {
    window.vueInstance = this; // 将 Vue 实例挂载到全局
    this.checkLoginStatus();
    document.title = '充值中心'; // 初始标题，fetchSystemSettings 返回后用站点名称替换
    this.fetchVipPackages(); // 获取VIP套餐数据
    this.getUserVipStatus(); // 获取用户VIP状态
    this.fetchRechargePackages(); // 获取点数充值套餐
    this.fetchUserBalance(); // 获取用户余额
    this.fetchSystemSettings(); // 获取系统设置（判断是否允许点数充值）
    //读取登录状态
    const loggedIn = localStorage.getItem('userLoggedIn') === 'true';
    const token = localStorage.getItem('token');
    if (loggedIn && token){
      this.isUserLoggedIn=true;
    }else{
      this.isUserLoggedIn=false;
    }
    // 应用保存的主题
    const savedTheme = localStorage.getItem('currentTheme') || 'default';
    document.body.className = `theme-${savedTheme}`;
  },
  methods: {
    // 切换主题
    handleThemeChange(theme) {
      localStorage.setItem('currentTheme', theme);
      document.body.className = 'theme-' + theme;
    },
    // 切换用户下拉菜单
    toggleUserDropdown() {
      this.userDropdownVisible = !this.userDropdownVisible;
    },
    // 返回上一页
    goBack() {
      this.$router.back();
    },
    // 退出登录
    handleLogout() {
      localStorage.removeItem('userLoggedIn');
      localStorage.removeItem('username');
      localStorage.removeItem('userEmail');
      this.$router.push({ name: 'TiktokQuery' });
    },
    // 关闭模态框
    closeQRCodeModal() {
      this.$Modal.remove(); // 手动移除当前显示的模态框
      this.$Message.success('已完成支付，模态框已关闭');
    },
    checkLoginStatus() {
      const isAuthenticated = localStorage.getItem('userLoggedIn') === 'true'; // 检查用户是否登录
      if (!isAuthenticated && this.$route.name !== 'TiktokQuery') {
        // 如果用户未登录且当前页面不是首页，则跳转到首页
        this.$router.push({ name: 'TiktokQuery' });
      }
    },
    // 打开充值列表页面的方法
    openRechargeList() {
      console.log('openRechargeList called, isUserLoggedIn:', this.isUserLoggedIn);

      // 如果用户未登录，显示登录模态框并提示
      if (!this.isUserLoggedIn) {
        this.$Message.warning('请先登录后再查看充值记录');
        this.showLoginModal = true;
        return;
      }

      // 用户已登录，使用 window.open 在新窗口中打开充值记录页面
      try {
        const routeUrl = this.$router.resolve({ name: 'RechargeList' });
        window.open(routeUrl.href, '_blank');
      } catch (error) {
        console.error('Failed to open recharge list page:', error);
        this.$Message.error('无法打开充值记录页面，请稍后重试');
      }
    },
    isWeixinBrowser() {
      const ua = navigator.userAgent.toLowerCase();
      return ua.indexOf('micromessenger') !== -1;
    },
    async generateAndShowQRCode(url) {
      try {
        const qrCodeImageUrl = await QRCode.toDataURL(url);
        this.showQRCodeModal(qrCodeImageUrl);
      } catch (err) {
        console.error('二维码生成失败:', err);
        this.$Message.error('二维码生成失败，请稍后重试');
      }
    },

    showQRCodeModal(qrCodeImageUrl, paymentMethod) {
      let title = '';
      let logoSrc = '';

      // 根据支付方式设置标题和 Logo
      if (paymentMethod === 'alipay') {
        title = '支付宝支付';
        logoSrc = require('@/assets/images/alipay_logo.png');
      } else if (paymentMethod === 'wxpay') {
        title = '微信支付';
        logoSrc = require('@/assets/images/wechat_logo.png');
      }

      // 确保支付链接有效
      if (!qrCodeImageUrl) {
        this.$Message.error('支付链接无效，无法生成二维码');
        return;
      }

      // 使用 QRCode.toDataURL 生成二维码图片
      QRCode.toDataURL(qrCodeImageUrl)
          .then((qrCodeBase64) => {
            console.log('Generated QR Code Base64:', qrCodeBase64);

            // 显示模态框
            this.$Modal.info({
              title: '',
              content: `            <div style="text-align: center; padding: 20px; border-radius: 8px; box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); border: 1px solid #ddd;">
              <!-- 顶部绿色标题栏 -->
              <div style="background-color: #e8f5e8; color: #388e3c; padding: 10px; border-radius: 8px; margin-bottom: 8px;">
                <img src="${logoSrc}" alt="${title}Logo" style="width: 24px; margin-right: 8px; vertical-align: middle;" />
                <span style="font-size: 16px; font-weight: bold;">${title}</span>
              </div>

              <!-- 金额显示 -->
              <div style="color: #388e3c; font-size: 20px; margin-bottom: 4px;">
                ¥${this.selectedAmount}              </div>

              <!-- 二维码图片 -->
              <img id="qrCodeImage" src="${qrCodeBase64}" alt="${title}二维码" style="width: 250px; height: 250px; margin-bottom: 8px;" />

              <!-- 系统订单号 -->
              <div style="background-color: #2d2d2d; color: white; padding: 10px; border-radius: 8px; margin-top: 8px;">
                <p style="margin: 0; font-size: 14px;">系统订单号：${this.orderId}</p>
                <p style="margin: 0; font-size: 14px;">请使用${paymentMethod === 'alipay' ? '支付宝' : '微信'}扫一扫</p>
                <p style="margin: 0; font-size: 14px;">扫描二维码支付</p>
              </div>
            </div>
          `,
              okText: '关闭',
            });
          })
          .catch((err) => {
            console.error('二维码生成失败:', err);
            this.$Message.error('二维码生成失败，请稍后重试');
          });
    },
    // 保存二维码到相册
    saveQrCode(qrCodeBase64) {
      const link = document.createElement('a'); // 创建一个 <a> 元素
      link.href = qrCodeBase64; // 设置下载链接为二维码图片的 Base64 URL
      link.download = `qrcode_${this.orderId}.png`; // 设置下载文件名
      link.click(); // 模拟点击下载

      // 提示用户保存成功
      this.$Message.success('二维码已保存到相册');
    },


    /**
     * 切换充值类型
     */
    switchRechargeType(type) {
      this.rechargeType = type;
      this.selectedPackage = null;
      this.selectedAmount = 0;
      this.customAmount = '';
    },
    /**
     * 打开支付确认 Modal
     */
    openPaymentConfirm() {
      if (this.rechargeType === 'vip' && !this.selectedPackage) {
        this.$Message.warning('请选择充值套餐');
        return;
      }
      if (this.rechargeType === 'points' && !this.selectedAmount) {
        this.$Message.warning('请选择充值金额');
        return;
      }
      this.showPayModal = true; // 显示支付确认 Modal
    },
    detectDeviceType() {
      const userAgent = navigator.userAgent.toLowerCase();
      if (/iphone|ipad|ipod|android/.test(userAgent)) {
        this.deviceType = 'mobile'; // 手机端
      } else {
        this.deviceType = 'pc'; // PC端
      }
    },
    selectPackage(pkg) { // 选择VIP套餐
      this.selectedPackage = pkg;
      this.selectedAmount = 0; // 清空点数选择
      this.customAmount = '';
    },
    selectPackageByAmount(amount) { // 选择点数套餐
      this.selectedAmount = amount; // 更新已选金额
      this.selectedPackage = null; // 清空VIP套餐选择
      this.customAmount = '';
    },
    showCustomAmountAlert() { // 输入自定义金额时的提示
      const amount = parseFloat(this.customAmount); // 转换为浮点数
      if (this.isFirstInput) { // 如果是首次输入
        this.$Message.info('请输入充值金额范围：10-1000元'); // 显示提示信息
        this.isFirstInput = false; // 标记已提示
      }
      this.selectedAmount = amount; // 动态更新已选金额
    },
    selectCustomAmount() { // 确认自定义金额
      if (this.customAmount && this.customAmount >= 5 && this.customAmount <= 1000) { // 校验金额范围
        this.selectedAmount = parseFloat(this.customAmount); // 更新已选金额
        this.selectedPackage = null; // 清空VIP套餐选择
      }
    },
    getPointsForAmount(amount) { // 根据金额计算点数
      if (this.rechargePackages[amount]) { // 如果是预设套餐
        return this.rechargePackages[amount]; // 返回套餐点数
      }
      if (amount <= 75) { // 小于等于75元
        return Math.floor(amount * 10); // 基础性价比（10点/元）
      }
      const tiers = [ // 定义分段奖励规则
        {min: 76, max: 200, rate: 1.5}, // 1.5倍
        {min: 201, max: 500, rate: 2}, // 2倍
        {min: 501, max: Infinity, rate: 3} // 3倍
      ];
      const tier = tiers.find(t => amount >= t.min && amount <= t.max); // 查找对应奖励倍率
      const rate = tier ? tier.rate : 1; // 默认1倍
      const basePoints = Math.floor(amount * 10); // 基础点数
      return Math.floor(basePoints * rate); // 应用奖励倍率
    },
    /**
     * 获取所有VIP套餐列表
     */
    async fetchVipPackages() {
      try {
        const response = await apiClient.get('/vip/packages');
        if (response.data.code === 200) {
          this.vipPackages = response.data.data;
          // 分类套餐：低请求版（daily_limit = 300）和高请求版（daily_limit = 450）
          this.lowRequestPackages = this.vipPackages.filter(pkg => pkg.daily_limit === 300);
          this.highRequestPackages = this.vipPackages.filter(pkg => pkg.daily_limit === 450);
        } else {
          this.$Message.error(response.data.msg || '获取VIP套餐列表失败');
        }
      } catch (error) {
        this.$Message.error('获取VIP套餐列表失败，请稍后重试');
        console.error('获取VIP套餐列表失败:', error);
      }
    },
    /**
     * 获取用户VIP状态
     */
    async getUserVipStatus() {
      try {
        const response = await apiClient.get('/vip/status');
        if (response.data.code === 200) {
          this.currentVipStatus = response.data.data;
        }
      } catch (error) {
        console.error('获取VIP状态失败:', error);
      }
    },
    /**
     * 获取套餐时长描述
     */
    getPackageDuration(pkg) {
      const name = pkg.name.toLowerCase();
      if (name.includes('月')) {
        return '30天/会员每天';
      } else if (name.includes('年')) {
        return '365天/会员每天';
      } else if (name.includes('终生') || name.includes('终身')) {
        return '永久会员';
      }
      return '';
    },
    /**
     * 获取系统设置
     */
    async fetchSystemSettings() {
      try {
        const response = await apiClient.get('admin/getsite');
        if (response.data.code === 200) {
          const data = response.data.data;
          this.siteInfo = data;
          this.allowPointRecharge = data.allow_point_recharge || false;
          // 使用后台站点名称动态设置浏览器标题
          document.title = (data.site_name || '抖数君') + '—充值中心';
          // 如果点数充值被禁用，自动切换到VIP充值
          if (!this.allowPointRecharge && this.rechargeType === 'points') {
            this.rechargeType = 'vip';
          }
        }
      } catch (error) {
        console.error('获取系统设置失败:', error);
      }
    },
    /**
     * 获取点数充值套餐
     */
    async fetchRechargePackages() {
      try {
        const response = await getRechargePackages();
        if (response.data.code === 200) {
          this.rechargePackages = response.data.data || {};
        } else {
          console.warn('获取点数充值套餐失败:', response.data.msg);
          // 设置默认套餐
          this.rechargePackages = {
            '10': 100,
            '20': 220,
            '50': 600,
            '100': 1300
          };
        }
      } catch (error) {
        console.error('获取点数充值套餐失败:', error);
        // 设置默认套餐
        this.rechargePackages = {
          '10': 100,
          '20': 220,
          '50': 600,
          '100': 1300
        };
      }
    },
    /**
     * 获取用户余额
     */
    async fetchUserBalance() {
      try {
        const response = await getUserBalance();
        if (response.data.code === 200) {
          this.userBalance = response.data.data.balance || 0;
        } else {
          console.warn('获取用户余额失败:', response.data.msg);
          this.userBalance = 0;
        }
      } catch (error) {
        console.error('获取用户余额失败:', error);
        this.userBalance = 0;
      }
    },
    // 创建充值订单 弃用
    async createOrder() {
      if (!this.selectedAmount) {
        this.$Message.warning('请选择充值金额');
        return;
      }

      try {
        this.isPaying = true;

        const response = await createRechargeOrder({
          amount: this.selectedAmount,
          points: this.getPointsForAmount(this.selectedAmount),
          type: this.paymentMethod,
          user_email: localStorage.getItem('userEmail') || '',
        });

        if (response.data.code === 1) {
          this.orderId = response.data.data.out_trade_no;
          this.payUrl = response.data.data.pay_url;
          this.showPayModal = true; // 显示支付确认模态框
        } else {
          this.$Message.error(response.data.msg || '订单创建失败');
        }
      } catch (error) {
        this.$Message.error('订单创建失败，请稍后重试');
      } finally {
        this.isPaying = false;
      }
    },
    /**
     * 确认支付
     */
    async confirmPay() {
      try {
        this.payModalLoading = true; // 开启加载状态

        let response;
        
        if (this.rechargeType === 'vip') {
          // VIP套餐充值
          response = await apiClient.post('/vip/create-order', {
            package_id: this.selectedPackage.id,// 套餐ID
            type: this.paymentMethod,// 支付方式
            user_email: localStorage.getItem('userEmail') || '',
            device_type: this.deviceType
          });
        } else {
          // 点数充值
          response = await createRechargeOrder({
            amount: this.selectedAmount,
            points: this.getPointsForAmount(this.selectedAmount),
            type: this.paymentMethod,// 支付方式
            user_email: localStorage.getItem('userEmail') || '',
            device_type: this.deviceType
          });
        }

        if (response.data.code === 1) {
          this.orderId = response.data.data.out_trade_no; // 获取订单ID
          if (this.deviceType === 'mobile') {
            this.qrcodeUrl = response.data.data.qrcode_url; // 提取 qrcode_url
            console.log('qrcodeUrl:', this.qrcodeUrl);
          } else {
            this.payUrl = response.data.data.pay_url; // 获取支付链接
            console.log('payUrl:', this.payUrl);
          }

          this.startPayment(); // 跳转到支付界面
          this.startPolling(); // 启动支付状态轮询
        } else {
          this.$Message.error(response.data.msg || '订单创建失败');
        }
      } catch (error) {
        this.$Message.error('订单创建失败，请稍后重试');
      } finally {
        this.payModalLoading = false; // 关闭加载状态
        this.showPayModal = false; // 隐藏支付确认 Modal
      }
    },
    /**
     * 开始支付流程
     */
    startPayment() {
      if (!this.payUrl && !this.qrcodeUrl) {
        this.$Message.error('支付链接无效，请重新创建订单');
        return;
      }

      if (this.deviceType === 'mobile') {
        if (this.paymentMethod === 'alipay') {
          // 支付宝支付逻辑
          const qrcodeUrl = this.qrcodeUrl; // 后端返回的 qrcode_url
          if (!qrcodeUrl) {
            this.$Message.error('二维码链接无效，请重新创建订单');
            return;
          }
          // 判断是否是微信浏览器 因为跳转不过去支付宝app，只能弹出支付二维码
          if (this.isWeixinBrowser()) {
            this.showQRCodeModal(qrcodeUrl, 'alipay'); // 传递 paymentMethod 参数
          } else {
            // 构造 alipays:// 跳转链接
            const alipayScheme = `alipays://platformapi/startapp?appId=20000067&url=${encodeURIComponent(qrcodeUrl)}`;
            window.location.href = alipayScheme; // 跳转到支付宝 App
          }
        } else if (this.paymentMethod === 'wxpay') {

            // 微信支付逻辑
            const wechatScheme = this.qrcodeUrl; // 后端返回的 weixin:// 链接
            if (!wechatScheme) {
              this.$Message.error('微信支付链接无效，请重新创建订单');
              return;
            }
            this.showQRCodeModal(wechatScheme, 'wxpay'); // 传递 paymentMethod 参数
        }
      } else {
        // PC 端逻辑：在新窗口打开支付页面
        if (this.paymentMethod === 'alipay') {
          if (!this.payUrl) {
            this.$Message.error('支付宝支付链接无效，请重新创建订单');
            return;
          }
          window.open(this.payUrl, '_blank'); // 打开支付宝支付页面
        } else if (this.paymentMethod === 'wxpay') {
          if (!this.payUrl) {
            this.$Message.error('微信支付链接无效，请重新创建订单');
            return;
          }
          window.open(this.payUrl, '_blank'); // 打开微信支付页面
        }
      }

      //this.startPolling(); // 启动支付状态轮询
    },
    // 启动支付倒计时
    startCountdown() {
      if (this.countdownInterval) return; // 如果倒计时已启动，则直接返回

      this.countdownTime = 600; // 初始化倒计时时间（10分钟）
      this.countdownInterval = setInterval(() => {
        if (this.countdownTime > 0) {
          this.countdownTime--;
        } else {
          clearInterval(this.countdownInterval); // 停止倒计时
          this.handlePaymentTimeout(); // 处理超时逻辑
        }
      }, 1000);
    },

    // 处理支付超时逻辑
    handlePaymentTimeout() {
      this.$Message.error('支付链接已失效，请重新创建订单');
      this.showPayModal = false; // 关闭支付确认模态框
      clearInterval(this.pollingInterval); // 停止轮询
    },
    // 启动支付状态轮询
    startPolling() {
      // 清理旧的轮询定时器
      this.cancelPolling();

      this.pollCount = 0; // 初始化轮询次数

      // 定义轮询函数
      const poll = async () => {
        try {
          // 如果达到最大轮询次数，停止轮询并处理超时逻辑
          if (this.pollCount >= this.maxPollAttempts) {
            this.handlePollTimeout();
            return;
          }

          // 检查订单号是否存在
          if (!this.orderId) {
            this.handleInvalidOrder();
            return;
          }

          // 调用 queryRechargeOrder 方法查询支付状态
          const response = await queryRechargeOrder({
            out_trade_no: this.orderId, // 传递订单ID
            user_email: localStorage.getItem('userEmail') || '', // 用户邮箱
          });

          // 检查响应数据
          if (response.data.code === 1) {
            const status = response.data.data.status;

            // 根据支付状态处理逻辑
            if (status === 1 || status === '1') {
              this.handlePaymentSuccess(); // 支付成功
              return ; // 提前退出
            } else if (status === 0) {
              console.log('订单未支付');
            } else {
              this.handlePaymentFail(); // 支付失败
              return ; // 提前退出
            }
          } else {
            console.warn('支付状态查询失败:', response.data.msg);
          }
        } catch (error) {
          console.error('支付状态轮询失败:', error);
          this.$Message.error('支付状态检查失败，请稍后重试');
        } finally {
          this.pollCount++; // 增加轮询计数

          // 如果达到最大轮询次数，强制停止轮询
          if (this.pollCount >= this.maxPollAttempts) {
            this.handlePollTimeout();
          }
        }
      };

      // 启动定时器，每5秒执行一次轮询
      this.pollingInterval = setInterval(poll, 5000);
    },
    // 取消支付状态轮询
    cancelPolling() {
      if (this.pollingInterval) {
        console.log('cancelPolling called, clearing interval');
        clearInterval(this.pollingInterval); // 确保定时器被清除
        this.pollingInterval = null; // 重置定时器引用
      }
    },
// 处理轮询超时逻辑
    handlePollTimeout() {
      clearInterval(this.pollingInterval); // 停止轮询
      this.cancelPolling(); // 清理轮询状态
      this.$Message.error('支付超时，请重新创建订单');
      this.handlePaymentTimeout(); // 处理支付超时逻辑
    },

// 处理无效订单逻辑
    handleInvalidOrder() {
      clearInterval(this.pollingInterval); // 停止轮询
      this.cancelPolling(); // 清理轮询状态
      this.$Message.error('订单号无效，请重新创建订单');
    },

    // 处理支付成功逻辑
    handlePaymentSuccess() {
      this.cancelPolling(); // 停止轮询
      this.paymentStatus = 'success';

      setTimeout(() => {
        console.log('支付成功：设置 payResult 为 success');
        this.payResult = 'success'; // 确保设置为成功状态
        this.resultMessage = '支付成功！您的余额已更新。';
        this.$Message.success(this.resultMessage);
        this.showPayModal = false; // 关闭支付确认模态框
        this.showResultModal = true; // 显示支付结果模态框
        this.fetchUserBalance(); // 更新用户余额
        this.resetPaymentState(); // 重置支付状态
        this.isCardDisabled = false; // 恢复主卡片状态
      }, 300); // 延迟 300ms，确保状态稳定
    },


    // 处理支付失败逻辑
    handlePaymentFail() {
      this.cancelPolling(); // 停止轮询
      this.paymentStatus = 'fail';

      this.payResult = 'fail';
      this.resultMessage = '支付失败，请重试。';
      this.$Message.error(this.resultMessage);
      this.showResultModal = true; // 显示支付结果模态框
      this.resetPaymentState(); // 重置支付状态
      this.isCardDisabled = false; // 恢复主卡片状态
    },
    // 重置支付状态
    resetPaymentState() {
      this.paymentStatus = 'pending'; // 重置支付状态
      this.payUrl = ''; // 清空支付链接
      this.orderId = ''; // 清空订单ID
      this.countdownTime = 0; // 重置倒计时时间
      clearInterval(this.countdownInterval); // 停止倒计时定时器
      this.countdownInterval = null; // 重置倒计时定时器引用
      this.pollCount = 0; // 重置轮询次数
    },
    cancelPay() { // 取消支付
      this.showPayModal = false; // 关闭支付确认模态框
      this.payModalLoading = false; // 关闭加载状态
      this.resetPaymentState()
    },

    closeResultModal() { // 关闭支付结果模态框
      setTimeout(() => {
        this.payResult = ''; // 清空支付结果
        this.resultMessage = '';
        this.showResultModal = false; // 隐藏模态框
        window.location.reload(); // 刷新页面
      }, 500); // 延迟 500ms 关闭，确保用户看到成功提示
    },
    retryPay() { // 重新支付
      this.showResultModal = false; // 关闭支付结果模态框
      this.showPayModal = true; // 显示支付确认模态框
    },
    resetSelection() { // 重置选择
      this.selectedPackage = null; // 清空已选VIP套餐
      this.selectedAmount = 0; // 清空已选金额
      this.customAmount = ''; // 清空自定义金额
      this.payUrl = ''; // 清空支付链接
    }
  },
  beforeDestroy() {
    this.cancelPolling(); // 清理轮询定时器
    clearInterval(this.countdownInterval); // 清理倒计时定时器
  },
};
</script>


<style scoped>

/* 移动端顶部导航栏样式 */
.mobile-top-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  margin-bottom: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.mobile-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mobile-header-left .menu-icon {
  font-size: 24px;
  color: #fff;
  cursor: pointer;
}

.mobile-header-left .logo-text {
  font-size: 18px;
  font-weight: bold;
  color: #fff;
}

.mobile-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mobile-user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  cursor: pointer;
}

.mobile-user-info .username {
  font-size: 14px;
  color: #fff;
  font-weight: 500;
}


/* 已选套餐信息样式 */
.selected-package {
  margin: 20px 0;
  padding: 15px;
  border-radius: 8px;
  background-color: #f0f8ff;
  /*border: 1px solid #d0e9ff;*/
}

/* 自定义金额容器 */
.custom-amount-container {
  display: flex; /* 弹性布局 */
  align-items: center; /* 垂直居中对齐 */
  gap: 10px; /* 子元素间距 */
  justify-content: center; /* 水平居中对齐 */
  margin-top: 10px;
}

/* 自定义金额标签（内联显示） */
.custom-amount-label-inline {
  font-size: 14px; /* 字体大小 */
  color: #515a6e; /* 文字颜色 */
  white-space: nowrap; /* 防止换行 */
}

/* recharge-page 样式 */
.recharge-page {
  padding: 20px; /* 内边距，避免内容紧贴边缘 */
  background-color: #f5f7fa; /* 背景色，提供柔和的视觉效果 */
  min-height: 100vh; /* 最小高度为视口高度，确保页面填满屏幕 */
  display: flex; /* 使用弹性布局 */
  flex-direction: column; /* 子元素垂直排列 */
  align-items: center; /* 水平居中对齐 */
  font-family: 'Arial', sans-serif; /* 字体设置 */
}

/* 页面标题样式 */
.recharge-page .page-title {
  text-align: center; /* 居中对齐 */
  font-size: 18px; /* 字体大小 */
  font-weight: bold; /* 加粗 */
  margin-bottom: 20px; /* 下边距 */
  color: #333; /* 文字颜色 */
}

/* 主卡片容器样式 */
.recharge-page .main-card {
  width: 100%; /* 占满父容器宽度 */
  max-width: 700px; /* 最大宽度限制 */
  margin: 0 auto; /* 水平居中 */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); /* 阴影效果 */
  border-radius: 10px; /* 圆角 */
  background-color: #fff; /* 白色背景 */
  padding: 20px; /* 内边距 */
  box-sizing: border-box; /* 盒模型 */
}

/* 自定义金额输入区域样式 */
.recharge-page .custom-amount {
  margin-top: 20px; /* 上边距 */
  text-align: center; /* 居中对齐 */
}

/* 支付方式样式 */
.recharge-page .payment-method {
  margin-top: 20px; /* 上边距 */
  text-align: center; /* 居中对齐 */
}

/* 倒计时样式 */
.recharge-page .countdown-timer {
  margin-top: 20px; /* 上边距 */
  text-align: center; /* 居中对齐 */
}

.recharge-page .countdown-timer p {
  font-size: 16px; /* 字体大小 */
  color: #ff4d4f; /* 红色文字，突出显示 */
  font-weight: bold; /* 加粗 */
}

/* 操作按钮区域样式 */
.recharge-page .actions {
  margin-top: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
}

.recharge-page .actions Button {
  min-width: 130px;
  height: 44px;
  font-size: 15px;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.recharge-page .btn-icon {
  margin-right: 6px;
  font-size: 16px;
}

.recharge-page .btn-pay {
  background: linear-gradient(135deg, #2d8cf0 0%, #5cadff 100%) !important;
  border: none !important;
  color: #fff !important;
  box-shadow: 0 4px 12px rgba(45, 140, 240, 0.3);
}

.recharge-page .btn-pay:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(45, 140, 240, 0.4);
}

.recharge-page .btn-pay:disabled {
  background: #c5c8ce !important;
  transform: none;
  box-shadow: none;
  color: #fff !important;
}

.recharge-page .btn-reset {
  background: transparent !important;
  border: 1px solid #2d8cf0 !important;
  color: #2d8cf0 !important;
}

.recharge-page .btn-reset:hover {
  background: #ecf5ff !important;
  color: #2d8cf0 !important;
  transform: translateY(-2px);
}

.recharge-page .btn-records {
  background: #f8f9fa !important;
  border: 1px solid #dcdee2 !important;
  color: #606266 !important;
}

.recharge-page .btn-records:hover {
  background: #f0f2f5 !important;
  color: #2d8cf0 !important;
  border-color: #2d8cf0 !important;
  transform: translateY(-2px);
}

/* 星空主题按钮覆盖 */
.theme-starry .recharge-page .btn-pay {
  background: linear-gradient(135deg, #64ffda 0%, #00bcd4 100%) !important;
  color: #0a0e27 !important;
  box-shadow: 0 4px 12px rgba(100, 255, 218, 0.3);
}

.theme-starry .recharge-page .btn-pay:hover {
  box-shadow: 0 6px 18px rgba(100, 255, 218, 0.4);
}

.theme-starry .recharge-page .btn-pay:disabled {
  background: #2a2a4e !important;
  color: #8892b0 !important;
}

.theme-starry .recharge-page .btn-reset {
  border-color: #64ffda !important;
  color: #64ffda !important;
}

.theme-starry .recharge-page .btn-reset:hover {
  background: #1a2745 !important;
  color: #64ffda !important;
}

.theme-starry .recharge-page .btn-records {
  background: #16213e !important;
  border-color: #2a2a4e !important;
  color: #ccd6f6 !important;
}

.theme-starry .recharge-page .btn-records:hover {
  background: #1a2745 !important;
  border-color: #64ffda !important;
  color: #64ffda !important;
}


/* 页面标题 */
.page-title {
  text-align: center; /* 居中对齐 */
  font-size: 24px; /* 字体大小 */
  font-weight: bold; /* 加粗 */
  margin-bottom: 20px; /* 下边距 */
  color: #333; /* 文字颜色 */
}

/* 主卡片样式 */
.main-card {
  border: 1px solid #e8eaec; /* 边框 */
  border-radius: 10px; /* 圆角 */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); /* 阴影 */
  padding: 20px; /* 内边距 */
  max-width: 700px; /* 最大宽度 */
  margin: 0 auto; /* 居中 */
}

/* 当前余额 */
.balance-info {
  text-align: center; /* 居中对齐 */
  font-size: 18px; /* 字体大小 */
  margin-bottom: 20px; /* 下边距 */
  color: #515a6e; /* 文字颜色 */
}

/* 充值类型切换 */
.recharge-type-switch {
  text-align: center;
  margin-bottom: 20px;
}

/* 套餐分类 */
.package-category {
  margin-bottom: 30px;
}

.category-title {
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #2d8cf0;
}

/* 套餐列表 */
.package-list {
  display: flex; /* 弹性布局 */
  flex-wrap: wrap; /* 自动换行 */
  gap: 15px; /* 间距 */
  justify-content: center; /* 居中对齐 */
}

.package-item {
  width: 180px; /* 宽度 */
  min-height: 140px; /* 最小高度 */
  border: 2px solid #e8eaec; /* 边框 */
  border-radius: 8px; /* 圆角 */
  text-align: center; /* 居中对齐 */
  padding: 15px; /* 内边距 */
  cursor: pointer; /* 鼠标指针 */
  transition: all 0.3s; /* 过渡动画 */
  background-color: #fff; /* 背景色 */
  box-sizing: border-box; /* 盒模型 */
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.package-item:hover {
  border-color: #2d8cf0; /* 鼠标悬停时边框颜色 */
  box-shadow: 0 4px 12px rgba(45, 140, 240, 0.2); /* 阴影 */
  transform: translateY(-2px);
}

.package-item.active {
  border-color: #2d8cf0; /* 激活状态下边框颜色 */
  background-color: #ecf5ff; /* 背景色 */
  box-shadow: 0 4px 12px rgba(45, 140, 240, 0.2); /* 阴影 */
}

.package-name {
  font-size: 14px;
  font-weight: bold;
  color: #333;
  margin-bottom: 8px;
}

.package-price {
  font-size: 24px; /* 字体大小 */
  font-weight: bold; /* 加粗 */
  color: #f60; /* 文字颜色 */
  margin-bottom: 8px; /* 下边距 */
}

.package-desc {
  font-size: 12px;
  color: #808695;
  margin-bottom: 5px;
}

.package-limit {
  font-size: 12px;
  color: #2d8cf0;
  font-weight: 500;
}



/* 自定义金额容器 */
.custom-amount-container {
  display: flex; /* 弹性布局 */
  align-items: center; /* 垂直居中 */
  justify-content: center; /* 水平居中 */
  gap: 10px; /* 间距 */
}

/* 支付方式 */
.payment-method {
  margin-top: 20px; /* 上边距 */
  text-align: center; /* 居中对齐 */
}

.payment-option {
  display: inline-flex; /* 行内弹性布局 */
  align-items: center; /* 垂直居中 */
  gap: 8px; /* 间距 */
  padding: 10px 15px; /* 内边距 */
  border: 1px solid #e8eaec; /* 边框 */
  border-radius: 8px; /* 圆角 */
  background-color: #fafafa; /* 背景色 */
  transition: all 0.3s; /* 过渡动画 */
  cursor: pointer; /* 鼠标指针 */
  margin-right: 20px; /* 右边距 */
}

.payment-option:hover {
  border-color: #2d8cf0; /* 鼠标悬停时边框颜色 */
  background-color: #ecf5ff; /* 背景色 */
  box-shadow: 0 4px 12px rgba(45, 140, 240, 0.2); /* 阴影 */
}

.payment-option.active {
  border-color: #2d8cf0; /* 激活状态下边框颜色 */
  background-color: #e6f7ff; /* 背景色 */
  box-shadow: 0 4px 12px rgba(45, 140, 240, 0.2); /* 阴影 */
}

.payment-option .ivu-icon {
  font-size: 24px; /* 图标大小 */
}

.payment-option span {
  font-size: 14px; /* 字体大小 */
  color: #515a6e; /* 文字颜色 */
}

.payment-logo {
  width: 40px; /* 宽度 */
  height: auto; /* 高度自动 */
  border-radius: 4px; /* 圆角 */
}




/* 倒计时样式 */
.countdown-timer {
  margin-top: 20px;
  text-align: center;
}

.countdown-timer p {
  font-size: 16px;
  color: #ff4d4f; /* 使用红色提示用户 */
  font-weight: bold;
}

.main-card.disabled {
  opacity: 0.6; /* 半透明效果 */
  pointer-events: none; /* 禁止鼠标交互 */
}



/* 响应式设计 */
@media (max-width: 768px) {
  .custom-amount-container {
    display: flex; /* 使用弹性布局 */
    flex-direction: row; /* 水平排列 */
    align-items: center; /* 垂直居中对齐 */
    gap: 10px; /* 子元素之间的间距 */
    flex-wrap: nowrap; /* 禁止换行 */
    justify-content: space-between; /* 左右对齐 */

  }

  .custom-amount-label-inline {
    white-space: nowrap; /* 防止文字换行 */
    margin-bottom: 0; /* 移除底部间距 */
  }
  .custom-amount-container Input {
    flex: 1; /* 输入框占据剩余空间 */
    max-width: 150px; /* 设置最大宽度，防止过长 */

  }

  .custom-amount-container Button {
    flex-shrink: 0; /* 防止按钮被压缩 */
    margin-top: 0; /* 移除顶部间距 */
  }

  .recharge-page {
    padding: 10px;
  }

  .recharge-page .main-card {
    padding: 15px;
    max-width: 100%;
  }

  .recharge-page .page-title {
    font-size: 20px;
  }

  .recharge-page .balance-info {
    font-size: 14px;
  }

  .recharge-type-switch {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .recharge-type-switch Button {
    width: 100%;
  }

  .category-title {
    font-size: 16px;
  }

  .package-list {
    flex-direction: column;
    gap: 10px;
  }

  .package-item {
    width: 100%;
    min-height: auto;
    padding: 12px;
  }

  .package-name {
    font-size: 14px;
  }

  .package-price {
    font-size: 18px;
  }

  .package-desc,
  .package-points,
  .package-unit-price {
    font-size: 12px;
  }

  .package-limit {
    font-size: 11px;
  }

  .custom-amount-container {
    flex-direction: column;
    align-items: stretch;
  }

  .custom-amount-container Input {
    width: 100%;
    max-width: 100%;
  }

  .payment-method {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .payment-option {
    flex-direction: row;
    justify-content: center;
    width: 100%;
    margin-right: 0;
  }

  .actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .actions Button {
    width: 100%;
    margin-left: 0 !important;
    margin-top: 0 !important;
  }

  .countdown-timer p {
    font-size: 14px;
  }

  /* 模态框移动端适配 */
  .pay-modal-content {
    padding: 10px;
  }

  .pay-modal-content p {
    font-size: 14px;
  }
}

/* 新增二维码保存提示样式 */
#saveQrCodeBtn {
  margin-top: 10px;
  padding: 5px 10px;
  background-color: #2d8cf0;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

#saveQrCodeBtn:hover {
  background-color: #1c7cd6;
}

/* 新增提示文字样式 */
.qrCodeTip {
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 5px;
}

/* 新增二维码图片样式 */
#qrCodeImage {
  width: 200px !important;
  height: auto !important;
  margin-bottom: 10px !important;
  display: block !important;
  border: 1px solid red; /* 调试边框 */
}

/**
 * 主题切换支持
 */

/* 星空主题（深色） */
.theme-starry .recharge-page {
  background-color: #0a0e27;
}

.theme-starry .main-card {
  background-color: #1a1a2e;
  border-color: #2a2a4e;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.theme-starry .page-title {
  background: linear-gradient(45deg, #64ffda, #00bcd4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.theme-starry .balance-info {
  color: #ccd6f6;
}

.theme-starry .category-title {
  color: #ccd6f6;
  border-bottom-color: #64ffda;
}

.theme-starry .package-item {
  background-color: #16213e;
  border-color: #2a2a4e;
  color: #ccd6f6;
}

.theme-starry .package-item:hover {
  border-color: #64ffda;
  box-shadow: 0 4px 12px rgba(100, 255, 218, 0.2);
}

.theme-starry .package-item.active {
  border-color: #64ffda;
  background-color: #1a2745;
}

.theme-starry .package-name {
  color: #ccd6f6;
}

.theme-starry .package-price {
  color: #64ffda;
}

.theme-starry .package-desc {
  color: #8892b0;
}

.theme-starry .package-limit {
  color: #64ffda;
}

.theme-starry .payment-option {
  background-color: #16213e;
  border-color: #2a2a4e;
}

.theme-starry .payment-option:hover {
  border-color: #64ffda;
  background-color: #1a2745;
}

.theme-starry .payment-option span {
  color: #ccd6f6;
}

.theme-starry .selected-package {
  background-color: #16213e;
}

.theme-starry .ivu-divider,
.theme-starry .ivu-divider-horizontal {
  color: #ccd6f6;
}

.theme-starry .ivu-divider-inner-text {
  background-color: #1a1a2e;
  color: #ccd6f6;
}

.theme-starry .ivu-divider::before,
.theme-starry .ivu-divider::after {
  border-color: #2a2a4e;
}

/* 蓝色主题 */
.theme-blue .recharge-page {
  background-color: #e3f2fd;
}

.theme-blue .main-card {
  background-color: #ffffff;
  border-color: #bbdefb;
}

.theme-blue .page-title {
  color: #1976d2;
}

.theme-blue .category-title {
  color: #1976d2;
  border-bottom-color: #1976d2;
}

.theme-blue .package-item:hover {
  border-color: #1976d2;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.2);
}

.theme-blue .package-item.active {
  border-color: #1976d2;
  background-color: #e3f2fd;
}

.theme-blue .package-price {
  color: #1976d2;
}

.theme-blue .package-limit {
  color: #1976d2;
}

.theme-blue .ivu-divider,
.theme-blue .ivu-divider-horizontal {
  color: #1976d2;
}

.theme-blue .ivu-divider-inner-text {
  background-color: #ffffff;
  color: #1976d2;
}

.theme-blue .ivu-divider::before,
.theme-blue .ivu-divider::after {
  border-color: #bbdefb;
}

/* 紫色主题 */
.theme-purple .recharge-page {
  background-color: #f3e5f5;
}

.theme-purple .main-card {
  background-color: #ffffff;
  border-color: #e1bee7;
}

.theme-purple .page-title {
  color: #7b1fa2;
}

.theme-purple .category-title {
  color: #7b1fa2;
  border-bottom-color: #7b1fa2;
}

.theme-purple .package-item:hover {
  border-color: #7b1fa2;
  box-shadow: 0 4px 12px rgba(123, 31, 162, 0.2);
}

.theme-purple .package-item.active {
  border-color: #7b1fa2;
  background-color: #f3e5f5;
}

.theme-purple .package-price {
  color: #7b1fa2;
}

.theme-purple .package-limit {
  color: #7b1fa2;
}

.theme-purple .ivu-divider,
.theme-purple .ivu-divider-horizontal {
  color: #7b1fa2;
}

.theme-purple .ivu-divider-inner-text {
  background-color: #ffffff;
  color: #7b1fa2;
}

.theme-purple .ivu-divider::before,
.theme-purple .ivu-divider::after {
  border-color: #e1bee7;
}

/* 绿色主题 */
.theme-green .recharge-page {
  background-color: #e8f5e9;
}

.theme-green .main-card {
  background-color: #ffffff;
  border-color: #c8e6c9;
}

.theme-green .page-title {
  color: #388e3c;
}

.theme-green .category-title {
  color: #388e3c;
  border-bottom-color: #388e3c;
}

.theme-green .package-item:hover {
  border-color: #388e3c;
  box-shadow: 0 4px 12px rgba(56, 142, 60, 0.2);
}

.theme-green .package-item.active {
  border-color: #388e3c;
  background-color: #e8f5e9;
}

.theme-green .package-price {
  color: #388e3c;
}

.theme-green .package-limit {
  color: #388e3c;
}

.theme-green .ivu-divider,
.theme-green .ivu-divider-horizontal {
  color: #388e3c;
}

.theme-green .ivu-divider-inner-text {
  background-color: #ffffff;
  color: #388e3c;
}

.theme-green .ivu-divider::before,
.theme-green .ivu-divider::after {
  border-color: #c8e6c9;
}

/* 红色主题 */
.theme-red .recharge-page {
  background-color: #ffebee;
}

.theme-red .main-card {
  background-color: #ffffff;
  border-color: #ffcdd2;
}

.theme-red .page-title {
  color: #d32f2f;
}

.theme-red .category-title {
  color: #d32f2f;
  border-bottom-color: #d32f2f;
}

.theme-red .package-item:hover {
  border-color: #d32f2f;
  box-shadow: 0 4px 12px rgba(211, 47, 47, 0.2);
}

.theme-red .package-item.active {
  border-color: #d32f2f;
  background-color: #ffebee;
}

.theme-red .package-price {
  color: #d32f2f;
}

.theme-red .package-limit {
  color: #d32f2f;
}

.theme-red .ivu-divider,
.theme-red .ivu-divider-horizontal {
  color: #d32f2f;
}

.theme-red .ivu-divider-inner-text {
  background-color: #ffffff;
  color: #d32f2f;
}

.theme-red .ivu-divider::before,
.theme-red .ivu-divider::after {
  border-color: #ffcdd2;
}

/* 橙色主题 */
.theme-orange .recharge-page {
  background-color: #fff3e0;
}

.theme-orange .main-card {
  background-color: #ffffff;
  border-color: #ffe0b2;
}

.theme-orange .page-title {
  color: #f57c00;
}

.theme-orange .category-title {
  color: #f57c00;
  border-bottom-color: #f57c00;
}

.theme-orange .package-item:hover {
  border-color: #f57c00;
  box-shadow: 0 4px 12px rgba(245, 124, 0, 0.2);
}

.theme-orange .package-item.active {
  border-color: #f57c00;
  background-color: #fff3e0;
}

.theme-orange .package-price {
  color: #f57c00;
}

.theme-orange .package-limit {
  color: #f57c00;
}

.theme-orange .ivu-divider,
.theme-orange .ivu-divider-horizontal {
  color: #f57c00;
}

.theme-orange .ivu-divider-inner-text {
  background-color: #ffffff;
  color: #f57c00;
}

.theme-orange .ivu-divider::before,
.theme-orange .ivu-divider::after {
  border-color: #ffe0b2;
}

/* 经典主题 */
.theme-classic .recharge-page {
  background-color: #f5f7fa;
}

.theme-classic .main-card {
  background-color: #ffffff;
  border-color: #e8eaec;
}

.theme-classic .page-title {
  color: #333;
}

.theme-classic .category-title {
  color: #333;
  border-bottom-color: #2d8cf0;
}

.theme-classic .ivu-divider,
.theme-classic .ivu-divider-horizontal {
  color: #333;
}

.theme-classic .ivu-divider-inner-text {
  background-color: #ffffff;
  color: #333;
}

.theme-classic .ivu-divider::before,
.theme-classic .ivu-divider::after {
  border-color: #e8eaec;
}
</style>

